<script setup lang="ts">
import { EStoreModules } from '@/store/storeType'
import type { ProductModel } from '@/types/models/ProductModel'
import {
    CreateButton,
    FullTableLayout,
    MobileShowFiltersButton,
    StackLayout
} from '@rfc/rfc-components-library'
import { useTableModel } from '@rfc/rfc-components-library/hooks'
import type { FullTableLayoutActionProps } from '@rfc/rfc-components-library/types'
import { getQueryFilters } from '@rfc/rfc-components-library/utils'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { PRODUCTS_ROUTES } from '../../../router/routes'
import { EProductActions } from '../../../store/productStore/ProductStoreTypes'
import CreateProductDialog from './CreateProductDialog.vue'

const { t } = useI18n()
const router = useRouter()
const store = useStore()

type Props = {
    actionProps?: FullTableLayoutActionProps<ProductModel>
    baseUrl?: string
}
defineProps<Props>()

const dialogCreateOpen = ref(false)
const columns = computed(() => [
    { title: t('products.columns.id'), key: 'id' },
    { title: t('products.columns.productId'), key: 'product_id' },
    { title: t('products.columns.name'), key: 'name' },
    { title: t('products.columns.encoding'), key: 'encoding' },
    {
        title: t('products.columns.rfidChipTemplate'),
        key: 'rfid_chip_template'
    },
    {
        title: t('products.columns.endRollLabelTemplate'),
        key: 'end_roll_label_template'
    }
])

const productsModel = useTableModel<ProductModel, { organization_id?: number }>({
    store,
    stateKey: EStoreModules.PRODUCT,
    stateAction: EProductActions.FETCH_PRODUCTS,
    router,
    dataFilters: {
        ...getQueryFilters()
    }
})

const setActionsProps: FullTableLayoutActionProps<ProductModel> = {
    titleItemKey: 'name',
    editPermission: {
        href: PRODUCTS_ROUTES.baseUrl,
        hasPermission: true
    }
}
</script>

<template>
    <StackLayout direction="column" :gap="16" class="w-full h-full pa-4">
        <FullTableLayout
            :columns="columns"
            :data="{ ...productsModel.data.value, ...productsModel.data.value?.meta }"
            :loading="productsModel.loading.value"
            :sortBy="productsModel.sort.value"
            :actionProps="{ ...setActionsProps, ...(actionProps ?? {}) }"
            @change:pageNumber="productsModel.handleChangePageNumber"
            @change:pageSize="productsModel.handleChangePageSize"
            @changeSortBy="productsModel.handleChangeSortBy"
            @removeTag="productsModel.handleRemoveTag"
            @search="productsModel.handleSearch"
            translationPath="event.filters"
            class="h-auto">
            <template #filters>
                <MobileShowFiltersButton>
                    <slot
                        name="filters"
                        :filters="productsModel.filters.value"
                        @change="productsModel.handleChangeFilters" />
                </MobileShowFiltersButton>
            </template>

            <template #button>
                <CreateButton :label="$t('products.create')" @click="dialogCreateOpen = true" />
            </template>

            <template #rfid_chip_template="item">
                <span class="text-caption">
                    {{ item.rfid_chip_template?.name ?? '—' }}
                </span>
            </template>

            <template #end_roll_label_template="item">
                <span class="text-caption">
                    {{ item.end_roll_label_template?.name ?? '—' }}
                </span>
            </template>
        </FullTableLayout>

        <CreateProductDialog
            :open="dialogCreateOpen"
            :routeToGo="baseUrl"
            @close="dialogCreateOpen = false"
            @refresh="productsModel.getData">
            <template #create="props">
                <slot name="create" :data="props.data" @change="props.onChange" />
            </template>
        </CreateProductDialog>
    </StackLayout>
</template>
